import { localDateString } from '../../../utils/helpers/localDate';
import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { notify } from '../../../utils/notification';

// Mirrors the backend limits for complaint attachments.
const MAX_FILES = 5;
const MAX_FILE_MB = 10;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const ComplaintForm = ({
    initial,
    enums,
    categories = [],
    // Society-scoped pool of assignable users. Parent fetches via
    // getUsernamesDropdown({ societyId }) and passes the resolved array here.
    assignableUsers = [],
    // Society-scoped list of flats with wing + owner details. Parent fetches via
    // getFlatsWithOwnership({ society_id }) — shape is what /flat-ownerships/
    // flats-with-ownership returns: [{ flat_id, flat_number, wing: { title, number },
    // ownerships: [{ entity: { first_name, last_name, company_name } }] }].
    flats = [],
    // Small note rendered under the flat picker, e.g. "Prefilled with your
    // flat". Parent decides when it applies.
    flatHint = null,
    onSubmit,
    onCancel,
    submitLabel = 'Save',
    submitting = false,
    mode = 'create',
    // Attachment intake is opt-in — Edit mode shouldn't offer it here since
    // attachments are managed on the detail view. onSubmit receives (payload, files).
    enableAttachments = false,
}) => {
    const [files, setFiles] = useState([]);

    const fullName = (u) => {
        if (!u) return '';
        const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
        return full || u.email || `User #${u.user_id}`;
    };

    // Compose "Wing A · Flat 101 — Priya Sharma" for the dropdown label so
    // staff pick by the human-readable location + owner rather than a raw ID.
    const flatLabel = (f) => {
        const wing = f?.wing?.title || f?.wing?.number || '';
        const flat = f?.flat_number ? `Flat ${f.flat_number}` : `#${f?.flat_id}`;
        const location = [wing && `Wing ${wing}`, flat].filter(Boolean).join(' · ');
        const owners = Array.isArray(f?.ownerships) ? f.ownerships : [];
        const ownerNames = owners
            .map(o => {
                const e = o?.entity;
                if (!e) return null;
                if (e.entity_type === 'COMPANY' && e.company_name) return e.company_name;
                return [e.first_name, e.last_name].filter(Boolean).join(' ').trim() || null;
            })
            .filter(Boolean);
        return ownerNames.length > 0 ? `${location} — ${ownerNames.join(', ')}` : location;
    };
    const [form, setForm] = useState({
        subject: '',
        description: '',
        category_id: '',
        priority: 'MEDIUM',
        due_date: '',
        flat_id: '',
        // Optional wing. Derived from the flat when one is picked; on its own it
        // raises a complaint against the whole wing (a lift, a corridor light).
        wing_id: '',
        // We identify the assignee by employee_id from the FE — the BE
        // resolves that to the underlying user_id server-side. Employees
        // whose user link is null won't appear as options anyway.
        employee_id: '',
        ...initial,
    });
    const [errors, setErrors] = useState({});

    // flats-with-ownership rows carry wing_id on the flat and the wing object
    // itself, so the wing list is derived here without another request.
    // Only employees with a linked login can be assignees: a complaint is assigned
    // to a user, and the API refuses an employee with no user_id. Employees
    // without one are counted so the form can explain why they are missing.
    const assignableEmployees = (assignableUsers || []).filter(u => u.employee_id && u.user_id);
    const unassignableCount = (assignableUsers || []).length - assignableEmployees.length;

    const wingIdOf = (f) => String(f?.wing?.wing_id ?? f?.wing_id ?? '');
    // Owner on record, or a current tenant / family member (both come back from
    // flats-with-ownership). Used only to label the option, never to hide it.
    const hasMembers = (f) =>
        (Array.isArray(f?.ownerships) && f.ownerships.length > 0)
        || (Array.isArray(f?.occupants) && f.occupants.length > 0);
    const wingOptions = useMemo(() => {
        const byId = new Map();
        (flats || []).forEach(f => {
            const id = wingIdOf(f);
            if (!id || byId.has(id)) return;
            const title = f?.wing?.title || f?.wing?.number || id;
            byId.set(id, { wing_id: id, label: `Wing ${title}` });
        });
        return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [flats]);

    // An edit opened on an older complaint may have a flat but no stored wing;
    // show the flat's wing rather than an empty picker.
    const selectedFlat = (flats || []).find(f => String(f.flat_id) === String(form.flat_id || ''));
    const effectiveWingId = String(form.wing_id || (selectedFlat ? wingIdOf(selectedFlat) : '') || '');

    const handleWingChange = (wingId) => {
        setForm(prev => {
            const flat = (flats || []).find(f => String(f.flat_id) === String(prev.flat_id || ''));
            // Keep the flat only if it belongs to the newly chosen wing.
            const keepFlat = flat && wingId && wingIdOf(flat) === String(wingId);
            return { ...prev, wing_id: wingId, flat_id: keepFlat ? prev.flat_id : '' };
        });
    };

    const handleFlatChange = (flatId) => {
        const flat = (flats || []).find(f => String(f.flat_id) === String(flatId));
        setForm(prev => ({
            ...prev,
            flat_id: flatId,
            // Picking a flat pins the wing to that flat's wing.
            wing_id: flat ? wingIdOf(flat) : prev.wing_id,
        }));
    };

    useEffect(() => {
        if (initial) setForm(f => ({ ...f, ...initial }));
    }, [initial]);

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        setErrors(e => ({ ...e, [k]: undefined }));
    };

    // Minimum acceptable due_date. For new complaints the raise date is
    // "today", so the due date can't be earlier. For edits we honour the
    // complaint's original raised_at so historical rows aren't blocked from
    // being tweaked upward.
    const today = localDateString();
    const minDueDate = initial?.raised_at
        ? String(initial.raised_at).slice(0, 10)
        : today;

    const validate = () => {
        const e = {};
        if (!form.subject?.trim()) e.subject = 'Subject is required';
        else if (form.subject.trim().length < 3) e.subject = 'Subject must be at least 3 characters';
        if (!form.description?.trim()) e.description = 'Description is required';
        else if (form.description.trim().length < 5) e.description = 'Description must be at least 5 characters';
        if (!form.category_id) e.category_id = 'Category is required';
        if (!form.priority) e.priority = 'Priority is required';
        if (form.due_date && form.due_date < minDueDate) {
            e.due_date = `Due date can't be earlier than ${minDueDate === today ? 'today' : 'the raised date'}`;
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        const payload = {
            subject: form.subject.trim(),
            description: form.description.trim(),
            category_id: Number(form.category_id),
            priority: form.priority,
        };
        if (form.due_date) payload.due_date = form.due_date;
        if (form.flat_id) payload.flat_id = Number(form.flat_id) || form.flat_id;
        // An edit must send the cleared flat, or the stored one stays and the
        // API refuses it against a newly chosen wing.
        else if (mode === 'edit') payload.flat_id = null;
        if (effectiveWingId) payload.wing_id = Number(effectiveWingId);
        else if (mode === 'edit') payload.wing_id = null;
        if (form.employee_id) payload.employee_id = Number(form.employee_id);
        onSubmit(payload, enableAttachments ? files : []);
    };

    // Picking files a second time used to REPLACE the first selection, so only
    // the last batch was ever uploaded. Each pick now adds to the list (same
    // behaviour as the comment box on the complaint detail view).
    const handleFilesChange = (e) => {
        const picked = Array.from(e.target.files || []);
        // Reset input so re-selecting the same file re-fires onChange.
        e.target.value = '';
        if (picked.length === 0) return;

        const tooBig = picked.filter(f => f.size > MAX_FILE_BYTES);
        if (tooBig.length) notify('error', `Each attachment must be under ${MAX_FILE_MB} MB`);
        const accepted = picked.filter(f => f.size <= MAX_FILE_BYTES);
        if (accepted.length === 0) return;

        setFiles(prev => {
            // De-dupe on name+size so picking the same file twice doesn't
            // burn one of the slots.
            const seen = new Set(prev.map(f => `${f.name}:${f.size}`));
            const merged = [...prev];
            accepted.forEach(f => {
                const key = `${f.name}:${f.size}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    merged.push(f);
                }
            });
            if (merged.length > MAX_FILES) {
                notify('error', `Too many files. Max ${MAX_FILES} attachments per complaint`);
                return merged.slice(0, MAX_FILES);
            }
            return merged;
        });
    };
    const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

    const priorities = enums?.priorities || [];
    const categoryOpts = Array.isArray(categories) ? categories : [];

    // If the resident hasn't set a due date, tell them what the SLA will
    // auto-apply based on priority so it isn't a black box on the backend.
    const slaHours = enums?.slaHours || {};
    const slaHint = (() => {
        if (form.due_date) return null;
        const h = slaHours[form.priority];
        if (!h) return null;
        const label = h < 48 ? `${h} hours` : `${Math.round(h / 24)} days`;
        return `Left blank — auto-set to ${label} from now (${form.priority} SLA).`;
    })();

    return (
        <form onSubmit={handleSubmit} className="tw-space-y-4">
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                <div className="sm:tw-col-span-2">
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">
                        Subject <span className="tw-text-rose-500">*</span>
                    </label>
                    <input
                        value={form.subject || ''}
                        onChange={e => set('subject', e.target.value)}
                        placeholder="Short summary"
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                    />
                    {errors.subject && <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.subject}</p>}
                </div>

                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">
                        Category <span className="tw-text-rose-500">*</span>
                    </label>
                    <select
                        value={form.category_id || ''}
                        onChange={e => set('category_id', e.target.value)}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                    >
                        <option value="">Select category…</option>
                        {categoryOpts.map(c => (
                            <option key={c.category_id} value={c.category_id}>{c.name}</option>
                        ))}
                    </select>
                    {categoryOpts.length === 0 && (
                        <p className="tw-text-[10px] tw-text-amber-600 tw-mt-1">
                            No categories yet — create one from Complaints → Categories.
                        </p>
                    )}
                    {errors.category_id && <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.category_id}</p>}
                </div>

                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">
                        Priority <span className="tw-text-rose-500">*</span>
                    </label>
                    <select
                        value={form.priority || ''}
                        onChange={e => set('priority', e.target.value)}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                    >
                        {priorities.map(p => <option key={p} value={p}>{humanize(p)}</option>)}
                    </select>
                    {errors.priority && <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.priority}</p>}
                </div>

                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Due Date</label>
                    <input
                        type="date"
                        value={form.due_date || ''}
                        onChange={e => set('due_date', e.target.value)}
                        min={minDueDate}
                        className={`tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 ${errors.due_date ? 'tw-border-rose-400' : 'tw-border-gray-200'}`}
                    />
                    {errors.due_date && <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.due_date}</p>}
                    {!errors.due_date && slaHint && (
                        <p className="tw-text-[10px] tw-text-blue-600 tw-mt-1 tw-flex tw-items-center tw-gap-1">
                            <Icon icon="mdi:clock-outline" /> {slaHint}
                        </p>
                    )}
                </div>

                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Wing (optional)</label>
                    <select
                        value={effectiveWingId}
                        onChange={e => handleWingChange(e.target.value)}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                    >
                        <option value="">Not tied to a specific wing</option>
                        {wingOptions.map(w => (
                            <option key={w.wing_id} value={w.wing_id}>{w.label}</option>
                        ))}
                    </select>
                    <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">
                        Pick a wing alone for an issue affecting the whole wing.
                    </p>
                </div>

                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Flat (optional)</label>
                    {(() => {
                        // Every active flat in the chosen wing is listed. The picker
                        // used to hide flats with no active owner record, which in
                        // practice emptied it: most flats here have no owner on
                        // file (A Wing: 13 flats, 0 listed), and a complaint about a
                        // flat is valid regardless — a leak from a vacant flat, say.
                        // The API accepts any active flat in the society. Flats with
                        // nobody registered are labelled rather than hidden.
                        //
                        // An already-selected flat (prefilled, or on an existing
                        // complaint) always stays visible; its wing drives
                        // effectiveWingId, so it is in the list anyway.
                        const selectedId = String(form.flat_id || '');
                        const eligibleFlats = flats.filter(
                            f => wingIdOf(f) === effectiveWingId || String(f.flat_id) === selectedId
                        );
                        return (
                            <>
                                <select
                                    value={form.flat_id || ''}
                                    onChange={e => handleFlatChange(e.target.value)}
                                    // Flats are chosen within a wing: the list is empty and
                                    // locked until a wing is picked above.
                                    disabled={!effectiveWingId}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed"
                                >
                                    <option value="">
                                        {effectiveWingId ? 'Whole wing — no specific flat' : 'Select a wing first'}
                                    </option>
                                    {eligibleFlats.map(f => (
                                        <option key={f.flat_id} value={f.flat_id}>
                                            {flatLabel(f)}{hasMembers(f) ? '' : ' — no members registered'}
                                        </option>
                                    ))}
                                </select>
                                {flatHint && form.flat_id && String(form.flat_id) === String(initial?.flat_id ?? '') && (
                                    <p className="tw-text-[10px] tw-text-blue-600 tw-mt-1 tw-flex tw-items-center tw-gap-1">
                                        <Icon icon="mdi:home-account" /> {flatHint}
                                    </p>
                                )}
                                {flats.length === 0 && (
                                    <p className="tw-text-[10px] tw-text-amber-600 tw-mt-1">
                                        No flats loaded for this society yet.
                                    </p>
                                )}
                                {flats.length > 0 && effectiveWingId && eligibleFlats.length === 0 && (
                                    <p className="tw-text-[10px] tw-text-amber-600 tw-mt-1">
                                        No flats have been added to this wing yet.
                                    </p>
                                )}
                                {flats.length > 0 && eligibleFlats.length < flats.length && (
                                    <p className="tw-text-[10px] tw-text-gray-500 tw-mt-1">
                                        {flats.length - eligibleFlats.length} flat(s) hidden — no members registered.
                                    </p>
                                )}
                            </>
                        );
                    })()}
                </div>

                {mode === 'create' && (
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Assign to Employee (optional)</label>
                        <select
                            value={form.employee_id || ''}
                            onChange={e => set('employee_id', e.target.value)}
                            className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                        >
                            <option value="">Auto (use category default)</option>
                            {assignableEmployees.map(u => (
                                <option key={u.employee_id} value={u.employee_id}>{fullName(u)}</option>
                            ))}
                        </select>
                        {/* The list only holds employees with a login account; say so when
                            that leaves people out, instead of showing a silent blank. */}
                        <p className={`tw-text-[10px] tw-mt-1 ${unassignableCount > 0 ? 'tw-text-amber-600' : 'tw-text-gray-500'}`}>
                            {assignableUsers.length === 0
                                ? 'No active employees found for this society.'
                                : assignableEmployees.length === 0
                                    ? `None of the ${assignableUsers.length} employees has a login account, so none can be assigned yet. Give an employee a login to assign complaints to them.`
                                    : unassignableCount > 0
                                        ? `${unassignableCount} employee(s) without a login account are not listed. Leave blank to use the category's default assignee.`
                                        : "Leave blank to fall back to the category's default assignee."}
                        </p>
                    </div>
                )}

                <div className="sm:tw-col-span-2">
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">
                        Description <span className="tw-text-rose-500">*</span>
                    </label>
                    <textarea
                        rows={5}
                        value={form.description || ''}
                        onChange={e => set('description', e.target.value)}
                        placeholder="Describe the issue in detail..."
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                    />
                    {errors.description && <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.description}</p>}
                </div>

                {enableAttachments && (
                    <div className="sm:tw-col-span-2">
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">
                            Attachments <span className="tw-text-gray-400 tw-font-normal">(optional — max {MAX_FILES} files, {MAX_FILE_MB} MB each)</span>
                        </label>
                        <label className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-6 tw-border-2 tw-border-dashed tw-border-gray-200 tw-rounded-lg tw-text-xs tw-text-gray-500 tw-cursor-pointer hover:tw-bg-gray-50 hover:tw-border-gray-300 tw-transition-colors">
                            <Icon icon="mdi:cloud-upload-outline" className="tw-text-lg" />
                            {files.length === 0
                                ? 'Click to attach photos or documents'
                                : `${files.length} of ${MAX_FILES} file(s) selected · click to add more`}
                            <input
                                type="file"
                                multiple
                                className="tw-hidden"
                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                onChange={handleFilesChange}
                            />
                        </label>
                        {files.length > 0 && (
                            <div className="tw-mt-2 tw-space-y-1.5">
                                {files.map((f, i) => (
                                    <div key={i} className="tw-flex tw-items-center tw-gap-2 tw-px-2.5 tw-py-1.5 tw-bg-gray-50 tw-rounded-md tw-text-[11px]">
                                        <Icon icon="mdi:paperclip" className="tw-text-gray-400" />
                                        <span className="tw-flex-1 tw-truncate tw-text-gray-700">{f.name}</span>
                                        <span className="tw-text-gray-400">{Math.round(f.size / 1024)} KB</span>
                                        <button
                                            type="button"
                                            onClick={() => removeFile(i)}
                                            className="tw-p-0.5 tw-rounded hover:tw-bg-gray-200 tw-text-gray-400"
                                            aria-label="Remove file"
                                        >
                                            <Icon icon="mdi:close" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2 tw-border-t tw-border-gray-100">
                <button type="button" onClick={onCancel} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                >
                    {submitting && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                    <Icon icon="mdi:content-save-outline" className="tw-text-sm" />
                    {submitLabel}
                </button>
            </div>
        </form>
    );
};

export default ComplaintForm;
